import { useCallback } from 'react';
import { Card, CardStatus } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H3, Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { Button, ButtonVariant, ButtonSize } from '@amzn/stencil-react-components/button';
import { IconBinSmall, IconExternalLinkSmall } from '@amzn/stencil-react-icons';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

import { truncateStyle } from '../../../shared/ui/truncateStyle';
import { TAPPABLE_CARD_STYLE } from '../../../shared/ui/ListCard';
import { useMessages } from '../../../shared/i18n/useMessages';
import { ReviewQuestionStatus, type ReviewQuestion } from '../logic/reviewTypes';

const PANEL_BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type PanelKey =
  | 'panelHeading'
  | 'panelCountSummary'
  | 'statusNeedsReview'
  | 'statusPublished'
  | 'questionNumberLabel'
  | 'deleteQuestionAltText';

/** Two-line ellipsis clamp for the card title. Module-level so the emotion
 *  css object is reference-stable across renders. */
const TITLE_CLAMP = truncateStyle(2);

/**
 * Selectable-draft card style. A plain `Card` rather than the shared
 * `ListCard`: that one is a real link (navigation), whereas selecting a draft
 * is an on-page action AND the card nests a delete button — Stencil's card
 * guidance says a card with interactive children must itself be a
 * non-interactive `Card`. The status border is shared so drafts sit
 * visually beside the audit/task list cards.
 *
 * TODO(audit-whiz): the selection is still a div `onClick`, so it is not
 * keyboard-operable. Making the body itself the control (rather than the
 * card) is the accessible shape; tracked as a follow-up so this CR stays a
 * no-behaviour-change refactor for this panel.
 */
const SELECTABLE_CARD_STYLE = {
  ...TAPPABLE_CARD_STYLE('color.status.neutral-fill'),
  cursor: 'pointer',
} as const;

/** Selected variant: the selectable card plus a muted background and an accent
 *  (primary-colored) left border, so the chosen draft is clearly highlighted —
 *  not just its bold title. Reuses the status left-border from
 *  {@link TAPPABLE_CARD_STYLE} (its width is a shared, already-sanctioned
 *  constant), recoloured to the info-fill accent, and adds the muted surface.
 *  Applied only to the currently-selected needs-review card. */
const SELECTED_CARD_STYLE = {
  ...TAPPABLE_CARD_STYLE('color.status.info-fill'),
  cursor: 'pointer',
  backgroundColor: token('color.surface.bg-muted') ?? '',
} as const;

/** Astra review status → Stencil BadgeType. Local to this feature — the
 *  shared ListCard status maps only cover audit/task statuses. */
const STATUS_BADGE_TYPE: Record<ReviewQuestionStatus, BadgeType> = {
  [ReviewQuestionStatus.NEEDS_REVIEW]: BadgeType.Neutral,
  [ReviewQuestionStatus.PUBLISHED]: BadgeType.Positive,
};

export interface AstraQuestionsPanelProps {
  readonly questions: readonly ReviewQuestion[];
  /** Currently-selected question id. */
  readonly selectedId: string;
  /** Called when a (non-published) question card is selected. */
  readonly onSelect: (id: string) => void;
  /** Called when an unpublished draft's delete control is used. */
  readonly onDelete: (id: string) => void;
  readonly dataTestId?: string;
}

interface QuestionCardProps {
  readonly question: ReviewQuestion;
  readonly index: number;
  readonly isSelected: boolean;
  readonly statusLabel: string;
  readonly numberLabel: string;
  readonly deleteAltText: string;
  readonly onSelect: (id: string) => void;
  readonly onDelete: (id: string) => void;
  readonly dataTestId?: string;
}

/** Body shared by both card variants: number label, title, status badge.
 *  Bolds the title when the card is the selected one (visible selection cue,
 *  since Card has no built-in selected style). */
function QuestionCardBody({
  question,
  numberLabel,
  statusLabel,
  isSelected,
  headerAction,
}: {
  readonly question: ReviewQuestion;
  readonly numberLabel: string;
  readonly statusLabel: string;
  readonly isSelected: boolean;
  /** Optional trailing control on the header line (e.g. the delete button).
   *  Placed in the number-label row so it stays anchored to the top-right of
   *  the card regardless of how many lines the title wraps to. */
  readonly headerAction?: React.ReactNode;
}): JSX.Element {
  const isPublished = question.status === ReviewQuestionStatus.PUBLISHED;
  return (
    <Col
      gridGap="dimensions.spacing.100"
      flex={1}
      minWidth={0}
      // Selected draft: bold title (below) is the visual cue; aria-current
      // carries the selection state for assistive tech.
      aria-current={isSelected ? 'true' : undefined}
    >
      <Row alignItems="center" gridGap="dimensions.spacing.100">
        <Text fontSize="T100" color="color.text.secondary">
          {isPublished && question.questionId ? question.questionId : numberLabel}
        </Text>
        {/* Decorative until the open-in-new-tab action is wired (later phase);
         *  aria-hidden so assistive tech isn't told about an action that does
         *  nothing yet.
         *  standards-ignore: cross-feature — IconExternalLinkSmall usage here differs semantically from features/task (decorative link-trailing); premature to share a wrapper */}
        {isPublished && <IconExternalLinkSmall aria-hidden="true" />}
        {/* Trailing action (delete) pinned to the right of the header line so
         *  it lines up across cards no matter the title's line count. */}
        {headerAction && (
          <>
            <Col flex={1} />
            {headerAction}
          </>
        )}
      </Row>
      <Text fontWeight={isSelected ? 'bold' : 'regular'} css={TITLE_CLAMP}>
        {question.title}
      </Text>
      <Row>
        <Badge type={STATUS_BADGE_TYPE[question.status]} value={statusLabel} />
      </Row>
    </Col>
  );
}

/** One question card. Published → non-clickable plain Card, no delete.
 *  Needs-review → selectable Card (status border) with a delete control. */
function QuestionCard({
  question,
  index,
  isSelected,
  statusLabel,
  numberLabel,
  deleteAltText,
  onSelect,
  onDelete,
  dataTestId,
}: QuestionCardProps): JSX.Element {
  const cardTestId = dataTestId ? `${dataTestId}-card-${index}` : undefined;

  const handleSelect = useCallback(() => onSelect(question.id), [onSelect, question.id]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      // Don't let the delete click also select the card.
      e.stopPropagation();
      onDelete(question.id);
    },
    [onDelete, question.id],
  );

  // Published: non-clickable, non-deletable. Needs review: selectable + deletable.
  const isPublished = question.status === ReviewQuestionStatus.PUBLISHED;

  // Delete control lives on the card header line (via headerAction) so it stays
  // top-right aligned across every card regardless of title wrap. Only rendered
  // for unpublished drafts.
  const deleteControl = isPublished ? undefined : (
    <Button
      variant={ButtonVariant.Tertiary}
      size={ButtonSize.Small}
      icon={<IconBinSmall aria-hidden="true" />}
      aria-label={deleteAltText}
      onClick={handleDelete}
      data-testid={dataTestId ? `${dataTestId}-delete-${index}` : undefined}
    />
  );

  return (
    <Card
      padding="dimensions.spacing.300"
      {...(cardTestId !== undefined ? { dataTestId: cardTestId } : {})}
      {...(isPublished
        ? { status: CardStatus.Success, statusIconAltText: statusLabel }
        : {
            css: isSelected ? SELECTED_CARD_STYLE : SELECTABLE_CARD_STYLE,
            onClick: handleSelect,
          })}
    >
      <QuestionCardBody
        question={question}
        numberLabel={numberLabel}
        statusLabel={statusLabel}
        isSelected={isSelected}
        headerAction={deleteControl}
      />
    </Card>
  );
}

/**
 * Right-side "Astra questions" panel for the Review & Publish screen.
 *
 * Renders a count summary + a vertical list of question cards. Needs-review
 * drafts are selectable and deletable; published questions render as
 * non-clickable cards (with their Q-ID + open-in-new-tab affordance) and
 * cannot be deleted from this view.
 *
 * Fully controlled — selection and deletion state live in the owning page/hook.
 */
export function AstraQuestionsPanel({
  questions,
  selectedId,
  onSelect,
  onDelete,
  dataTestId,
}: AstraQuestionsPanelProps): JSX.Element {
  const { t, f } = useMessages<PanelKey>(PANEL_BUNDLE);

  const pendingCount = questions.filter(
    (q) => q.status === ReviewQuestionStatus.NEEDS_REVIEW,
  ).length;

  return (
    <Card
      padding="dimensions.spacing.300"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.200" width="100%">
        <H3>{t('panelHeading')}</H3>
        <Text fontSize="T100" color="color.text.secondary">
          {f('panelCountSummary', { total: questions.length, pending: pendingCount })}
        </Text>

        {questions.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            index={index}
            isSelected={question.id === selectedId}
            statusLabel={
              question.status === ReviewQuestionStatus.PUBLISHED
                ? t('statusPublished')
                : t('statusNeedsReview')
            }
            numberLabel={f('questionNumberLabel', { number: index + 1 })}
            deleteAltText={t('deleteQuestionAltText')}
            onSelect={onSelect}
            onDelete={onDelete}
            {...(dataTestId ? { dataTestId } : {})}
          />
        ))}
      </Col>
    </Card>
  );
}
